import { useEffect } from "react";
import { IInvoice, useInvoiceStore } from "../../stores/invoice";
import {
  calculateTotalHTGen,
  calculateTotalTTCGen,
} from "../../services/function";

const TableDetailsProforma = () => {
  // Store
  const {
    detailsInvoice,
    setDetailsInvoice,
    formData,
    totalHT,
    setTotalHT,
    totalTTC,
    setTotalTTC,
    tabInvoice
  } = useInvoiceStore();

  const handleAddNewLibelle = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    setDetailsInvoice([
      ...detailsInvoice,
      {
        text: "",
        amount: 0,
      } as IInvoice, // Ajout d'un nouvel objet vide pour l initialisation
    ]);
  };

  const handleChange = (index: number, value: string) => {
    const updatedDetails = [...detailsInvoice];
    updatedDetails[index].text = value;
    setDetailsInvoice(updatedDetails);
  };

  const handleChangeAmount = (index: number, value: any) => {
    const updatedDetails = [...detailsInvoice];
    if (value === "") {
      updatedDetails[index].amount = 0;
    } else {
      // Stocker la valeur telle quelle (avec virgule ou point)
      updatedDetails[index].amount = value;
    }
    setDetailsInvoice(updatedDetails);
  };

  const deleteLibelle = (index: number) => {
    const updatedDetails = [...detailsInvoice];
    updatedDetails.splice(index, 1);
    setDetailsInvoice(updatedDetails);
  };

  // Calcul du total HT
  const calculateTotalHT = () => {
    // Convertir les valeurs avec virgule en point pour le calcul
    const normalizedDetails = detailsInvoice.map(item => ({
      ...item,
      amount: typeof item.amount === 'string' 
        ? parseFloat(item.amount.replace(',', '.')) || 0
        : item.amount || 0
    }));
    
    const total = calculateTotalHTGen(normalizedDetails, "amount");
    setTotalHT(total);
  };

  // Calcul du total TTC
  const calculateTotalTTC = () => {
    if (isNaN(totalTTC)) {
      setTotalTTC(0);
    } else {
      if (formData.exonerated) {
        setTotalTTC(totalHT);
      } else {
        // Convertir les valeurs avec virgule en point pour le calcul
        const normalizedDetails = detailsInvoice.map(item => ({
          ...item,
          amount: typeof item.amount === 'string' 
            ? parseFloat(item.amount.replace(',', '.')) || 0
            : item.amount || 0
        }));
        
        const total = calculateTotalTTCGen(
          normalizedDetails,
          "amount",
          formData.exonerated
        );
        setTotalTTC(total);
      }
    }
  };

  // Met a jour le total HT et TTC a chaque changement
  useEffect(() => {
    calculateTotalHT();
    calculateTotalTTC();
  }, [detailsInvoice, formData.exonerated, totalHT]);

  return (
    <div className="w-full flex flex-col justify-start items-start bg-white rounded-xl shadow-lg p-6 mt-4">
      <div className="w-full flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Détails de {tabInvoice === "Factures" ? "l'avoir" : "la facture"}</h3>
        <button
          onClick={handleAddNewLibelle}
          className="bg-linear-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-4 py-2 rounded-lg font-medium transition-all duration-300 font-Nunito shadow-md hover:shadow-lg flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Ajouter une ligne
        </button>
      </div>
      
      <div className="w-full overflow-hidden rounded-lg border border-gray-200 shadow-sm ">
        <table className="w-full">
          <thead className="bg-linear-to-r from-gray-50 to-gray-100">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 border-b border-gray-200">
                Libellé
              </th>
              <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700 border-b border-gray-200 w-48">
                Montant HT
              </th>
              <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700 border-b border-gray-200 w-16">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {detailsInvoice.map((invoice, i) => (
              <tr key={i} className="hover:bg-gray-50 transition-colors duration-150">
                <td className="px-6 py-4">
                  <textarea
                    value={invoice.text}
                    onChange={(e) => handleChange(i, e.target.value)}
                    name="text"
                    placeholder="Saisissez votre libellé..."
                    className="w-full border border-gray-300 rounded-lg p-3 bg-white text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 min-h-[80px] max-h-24"
                    rows={3}
                  />
                </td>
                <td className="px-6 py-4">
                  <div className="flex justify-center">
                    <div className="relative">
                      <input
                        type="text"
                        value={invoice.amount || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          handleChangeAmount(i, value);
                        }}
                        name="amount"
                        placeholder="Montant"
                        className="w-32 text-center border border-gray-300 rounded-lg p-3 bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      />
                      <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">€</span>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex justify-center">
                    <button 
                      onClick={() => deleteLibelle(i)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-full transition-all duration-200"
                      title="Supprimer cette ligne"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {/* Totals Section */}
        <div className="bg-gray-50 border-t border-gray-200">
          <div className="px-6 py-4">
            <div className="flex justify-between items-center py-2">
              <span className="text-gray-700 font-medium">Total HT</span>
              <span className="text-gray-900 font-semibold text-lg">{totalHT?.toFixed(2)} €</span>
            </div>
            
            {formData.exonerated && (
              <div className="flex justify-between items-center py-2">
                <span className="text-gray-600 text-sm italic">Exonéré de TVA</span>
                <span className="text-gray-500 text-sm">0.00 €</span>
              </div>
            )}
            
            <div className="flex justify-between items-center py-3 border-t border-gray-200 mt-2">
              <span className="text-gray-800 font-bold text-lg">Total TTC</span>
              <span className="text-blue-600 font-bold text-xl">{totalTTC?.toFixed(2)} €</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TableDetailsProforma;
